import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Field, MultilangField } from "@/pages/admin/AdminOffers";

const empty = {
    question: { de: "", tr: "", en: "" },
    answer: { de: "", tr: "", en: "" },
    order: 0,
    active: true,
};

export default function AdminFaqs() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = () => api.get("/admin/faqs").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (it) => {
        setEditing(it);
        setForm({
            question: it.question || { de: "", tr: "", en: "" },
            answer: it.answer || { de: "", tr: "", en: "" },
            order: it.order || 0,
            active: it.active !== false,
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) await api.put(`/admin/faqs/${editing.id}`, form);
            else await api.post("/admin/faqs", form);
            toast.success("Gespeichert");
            setOpen(false);
            load();
        } catch { toast.error("Fehler beim Speichern"); }
    };

    const del = async (it) => {
        if (!confirm("Wirklich löschen?")) return;
        await api.delete(`/admin/faqs/${it.id}`);
        toast.success("Gelöscht");
        load();
    };

    return (
        <div className="p-6 md:p-10 max-w-5xl">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-brand-green">FAQ</div>
                    <h1 className="mt-1 font-display text-3xl font-bold">Häufige Fragen</h1>
                </div>
                <button onClick={startCreate} className="btn-primary" data-testid="faq-create"><Plus className="h-4 w-4" />Neue Frage</button>
            </div>

            <div className="mt-6 card-clean divide-y divide-black/5">
                {items.map((it) => (
                    <div key={it.id} className="p-5 flex items-start gap-4" data-testid={`faq-row-${it.id}`}>
                        <div className="text-xs font-mono text-brand-charcoal/40 w-8">{it.order}</div>
                        <div className="flex-1">
                            <div className="font-medium">{it.question?.de}</div>
                            <div className="mt-1 text-sm text-brand-charcoal/60">{it.answer?.de}</div>
                        </div>
                        <button onClick={() => startEdit(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-brand-cream-dark"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-brand-cream-dark text-brand-red"><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
                {items.length === 0 && <div className="p-10 text-center text-sm text-brand-charcoal/60">Noch keine FAQ-Einträge.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editing ? "FAQ bearbeiten" : "Neue FAQ"}</DialogTitle></DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <MultilangField label="Frage" value={form.question} onChange={(v) => setForm((f) => ({ ...f, question: v }))} />
                        <MultilangField label="Antwort" value={form.answer} onChange={(v) => setForm((f) => ({ ...f, answer: v }))} multiline />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Reihenfolge"><input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value || "0") }))} className="input-clean" /></Field>
                            <Field label="Status"><label className="inline-flex items-center gap-2 mt-3"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Aktiv</label></Field>
                        </div>
                        <DialogFooter className="pt-2">
                            <button type="button" className="btn-outline-dark" onClick={() => setOpen(false)}>Abbrechen</button>
                            <button type="submit" className="btn-primary" data-testid="faq-save">Speichern</button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
